<template>
  <div class="app-step-two-tabs-writing">
    <div class="app-step-two-tabs__wrapper">
      <div
        v-if="!isRequiremenstsFilled"
        class="app-step-two-tabs-writing__warning"
      >
        <span
          >Please upload a document or submit an answer - at least five
          characters - to one or more of the writing questions below</span
        >
      </div>
      <div class="app-step-two-tabs-upload-row">
        <div class="upload-documentation">
          <div class="upload-documentation__left">
            <p>
              Upload your supporting documentation for your press release
              (including contact information):
            </p>

            <p>
              <b v-if="state.writingDocumentation?.name">
                Uploaded Document:
              </b>
              {{ state.writingDocumentation?.name }}
            </p>
          </div>
          <div class="upload-documentation__right">
            <app-file-selector
              refName="writingDocumentation"
              accept=".rtf, .doc, .docx, .txt"
              v-model:file="state.writingDocumentation"
            >
            </app-file-selector>
          </div>
        </div>
      </div>
      <div class="app-step-two-tabs-upload-row">
        <p>
          What current news do you wish to announce? (New website, new product,
          increase in sales, merger, new license or contract, recent awards,
          etc.)
        </p>
        <step-input
          v-model="state.writingQuestions.current"
          :validation-model="v$.writingQuestions.current"
          textarea
        ></step-input>
      </div>

      <div class="app-step-two-tabs-upload-row">
        <p>
          What is unique about your company, service or product(s)? What makes
          your product/service different than your competitors? (better quality,
          lower prices, service or product that no one else offers on web,
          unique website features such as advanced searches or great links, etc.
        </p>
        <step-input
          v-model="state.writingQuestions.unique"
          :validation-model="v$.writingQuestions.unique"
          textarea
        ></step-input>
      </div>

      <div class="app-step-two-tabs-upload-row">
        <p>
          Details about company - how long in business, how long on web,
          location, affiliations, services offered, awards and recognition,
          website address, contact information, etc.
        </p>
        <step-input
          v-model="state.writingQuestions.details"
          :validation-model="v$.writingQuestions.details"
          textarea
        ></step-input>
      </div>

      <div class="app-step-two-tabs-upload-row">
        <p>Plans for future of company, products.</p>
        <step-input
          v-model="state.writingQuestions.plans"
          :validation-model="v$.writingQuestions.plans"
          textarea
        ></step-input>
      </div>

      <div class="app-step-two-tabs-upload-row">
        <p>
          Quotes from company officers or others commenting on company or
          products (We can make quotes up, given enough of above information)
        </p>
        <step-input
          v-model="state.writingQuestions.quotes"
          :validation-model="v$.writingQuestions.quotes"
          textarea
        ></step-input>
      </div>

      <div class="app-step-two-tabs-upload-row">
        <p>Other relevant information.</p>
        <step-input
          v-model="state.writingQuestions.other"
          :validation-model="v$.writingQuestions.other"
          textarea
        ></step-input>
      </div>
    </div>

    <navigation-buttons
      :disabled-outer-condition="isLoading || !isRequiremenstsFilled"
      :loading="isLoading"
      @onPreviousButtonClick="handlePreviousClick"
      @onNextButtonClick="handleNextClick"
    ></navigation-buttons>
  </div>
</template>

<script setup>
import _ from 'lodash-es'

import { watch, computed } from 'vue'
import { maxLength } from '@vuelidate/validators'
import { useOrder } from '@core/composables/order/useOrder'
import { state } from './state'
import { useNavigationButtons } from '@modules/app-steps/step-two/navigation-buttons'
import { useTabsStore } from '@/stores/tabs'

import AppFileSelector from '@modules/ui/app-file-selector.vue'
import NavigationButtons from '@modules/app-steps/step-two/navigation-buttons/index.vue'
import StepInput from '@modules/app-steps/step-input.vue'
import useVuelidate from '@vuelidate/core'
import { useToast } from 'vue-toastification'

const toast = useToast()
const MAX_FIELD_LENGTH = 10000
const MIN_FIELD_LENGTH = 5
const allowedExtensions = ['.doc', '.docx', '.rtf', '.txt']
const rules = computed(() => ({
  writingQuestions: {
    current: { maxLength: maxLength(MAX_FIELD_LENGTH) },
    unique: { maxLength: maxLength(MAX_FIELD_LENGTH) },
    details: { maxLength: maxLength(MAX_FIELD_LENGTH) },
    plans: { maxLength: maxLength(MAX_FIELD_LENGTH) },
    quotes: { maxLength: maxLength(MAX_FIELD_LENGTH) },
    other: { maxLength: maxLength(MAX_FIELD_LENGTH) },
  },
}))

const isAnyFieldFilled = computed(() => {
  const { current, unique, details, plans, quotes, other } =
    state.writingQuestions
  return [current, unique, details, plans, quotes, other].some(
    (field) => field?.length >= MIN_FIELD_LENGTH
  )
})

const isDocumentUploaded = computed(() => {
  const document = state.writingDocumentation

  return !!document?.name
})

const isRequiremenstsFilled = computed(() => {
  return isAnyFieldFilled.value || isDocumentUploaded.value
})

const { getFrontendTabBuilder } = useTabsStore()

const { useUpdateOrder } = useOrder()
const { isLoading, updateOrder } = useUpdateOrder()
const { navigate } = useNavigationButtons()

const v$ = useVuelidate(rules, state)

const pruneObject = (obj) => {
  return JSON.parse(JSON.stringify(obj), (key, value) => {
    if (value == null || value == '' || value == [] || value == {})
      return undefined
    return value
  })
}

const payload = { ..._.omit(state, ['writingDocumentation']) }

const handlePreviousClick = () => {
  updateOrder({ ...pruneObject(payload), ...getFrontendTabBuilder() }, () =>
    navigate()
  )
}

const handleFileUpload = () => {
  const file = state.writingDocumentation
  if (file) {
    const fileName = file.name.toLowerCase()
    const isValid = allowedExtensions.some((ext) => fileName.endsWith(ext))

    if (!isValid) {
      state.writingDocumentation = null
      toast.error('Invalid file type. Allowed: .doc, .docx, .rtf, .txt')
    } else {
      updateOrder({ writingDocumentation: state.writingDocumentation })
    }
  }
}

const handleNextClick = async () => {
  const noErrors = await v$.value.$validate()
  if (!noErrors) return

  updateOrder({ ...pruneObject(payload), ...getFrontendTabBuilder(true) }, () =>
    navigate(true)
  )
}

watch(() => state.writingDocumentation, handleFileUpload)
</script>
